import { createTheme } from '@mui/material/styles';

import { components } from './components';
import { colors, radius, semantic } from './tokens';
import { typography } from './typography';

/**
 * The TimeLog look, mapped onto MUI from the design tokens (tokens.css /
 * tokens.ts). Buttons are pills, surfaces are flat and outlined, numbers render
 * in Geist Mono where components opt into a `…Mono` typography variant.
 *
 * Reference implementation: Web/src/assets/themes/. Palette slots follow
 * createTimelogTheme.ts, the type scale follows typography.ts, and the component
 * overrides follow components/Mui*.ts — see ./components.ts.
 *
 * See docs/adr/0009-timelog-design-tokens-and-theme.md and
 * docs/adr/0012-align-with-the-timelog-design-system.md.
 */
export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: colors.pink[80], dark: colors.pink[100], light: colors.pink[40] },
    /**
     * Fog, not purple. The design system's `secondary` is the cool neutral it
     * uses for chrome and secondary buttons; purple is an accent scale that
     * `secondary` never pointed at in Web.
     */
    secondary: { main: colors.fog[80], dark: colors.fog[100], light: colors.fog[40] },
    success: { main: colors.success[80], dark: colors.success[100], light: colors.success[10] },
    warning: { main: colors.warning[80], dark: colors.warning[100], light: colors.warning[10] },
    error: { main: colors.error[80], dark: colors.error[100], light: colors.error[10] },
    info: { main: colors.info[80], dark: colors.info[100], light: colors.info[10] },
    background: { default: semantic.bgApp, paper: semantic.bgPanel },
    text: { primary: semantic.fg1, secondary: semantic.fg2, disabled: semantic.fgDisabled },
    divider: semantic.borderDivider,
  },
  /**
   * 4px, matching the design system's `--border-radius-small` base. Components
   * that want more say so themselves (Card 12px, Dialog/Paper 6px, Button pill)
   * rather than inheriting a large default — the previous value of 8 quietly
   * rounded everything that never asked.
   */
  shape: { borderRadius: radius.small },
  typography,
  components,
});
